<template>
  <CCard
    :bordered="false"
    class="c-file-viewer-bottom px-8 py-4 justify-center h-[76px] left-0 right-0 bottom-0 absolute"
    :class="{ 'c-file-viewer-bottom--is-fullscreen': isFullscreen }"
    @click="emit('click')"
  >
    <div class="flex justify-between">
      <div class="flex items-center gap-2">
        <div
          v-if="!hideActions"
          class="flex justify-center items-center rd"
          :class="{
            'c-file-viewer-bottom__annotation-select-bordered rd': isAnnotationsSelected,
            'c-file-viewer-bottom__annotation-select': !isAnnotationsHovered,
            'c-file-viewer-bottom__annotation-select-hover': isAnnotationsHovered,
          }"
          @mouseleave="isAnnotationsHovered = false"
          @mouseover="isAnnotationsHovered = true"
        >
          <CButton
            v-tooltip="t('canvasUI.components.fileViewer.draw')"
            :class="{
              'c-file-viewer-bottom__annotation-select': !isAnnotationsHovered,
              'c-file-viewer-bottom__annotation-select-hover': isAnnotationsHovered,
            }"
            secondary
            size="large"
            @click="emit('toggleAnnotations')"
          >
            <template #icon>
              <CIcon :icon="annotationConfig[selectedAnnotationType].icon" size="16" />
            </template>
          </CButton>
          <CDropdown
            class="rd c-file-viewer-bottom__annotation-dropdown"
            :consistentMenuWidth="false"
            :options="annotationOptions"
            :renderLabel="renderLabel"
            :to="rootElement"
            trigger="click"
            @select="emit('selectAnnotation', $event)"
          >
            <div>
              <CIcon
                v-tooltip="t('canvasUI.components.fileViewer.draw')"
                class="cursor-pointer mr-2 c-file-viewer-bottom__annotation-dropdown-trigger"
                faType="fas"
                icon="angle-down"
                size="16px"
              />
            </div>
          </CDropdown>
        </div>
        <CButton
          v-if="!hideActions"
          v-tooltip="t('canvasUI.components.fileViewer.textAnnotations')"
          class="c-file-viewer-bottom__button"
          :class="{ 'c-file-viewer-bottom__button--selected': isTextAnnotationSelected }"
          secondary
          size="large"
          @click="emit('toggleTextAnnotation')"
        >
          <template #icon>
            <CIcon :icon="annotationConfig.text.icon" size="16" />
          </template>
        </CButton>
        <CButton
          v-if="!hideActions && clientType === 'admin' && !isOverlay"
          v-tooltip="t('canvasUI.components.fileViewer.linkAnnotations')"
          class="c-file-viewer-bottom__button"
          :class="{ 'c-file-viewer-bottom__button--selected': isLinkAnnotationSelected }"
          secondary
          size="large"
          @click="emit('toggleLinkAnnotation')"
        >
          <template #icon>
            <CIcon :icon="annotationConfig.link.icon" size="16" />
          </template>
        </CButton>
        <CButton
          v-tooltip="t('canvasUI.components.fileViewer.overview')"
          class="c-file-viewer-bottom__button"
          :class="{ 'c-file-viewer-bottom__button--selected': isThumbnailsSelected }"
          secondary
          size="large"
          @click="emit('toggleThumbnails')"
        >
          <template #icon>
            <CIcon icon="grid-2" size="16" />
          </template>
        </CButton>
        <CButton
          v-tooltip="t('canvasUI.components.fileViewer.searchInFile')"
          class="c-file-viewer-bottom__button"
          :class="{ 'c-file-viewer-bottom__button--selected': isSearchSelected }"
          secondary
          size="large"
          @click="emit('searchFile')"
        >
          <template #icon>
            <CIcon icon="file-magnifying-glass" size="16" />
          </template>
        </CButton>
        <NSwitch
          v-if="hasHotspotsOnCurrentPage && isHotspotToggleEnabled && clientType === 'impact'"
          v-tooltip="
            isAnnotationVisibilityEnabled
              ? t('canvasUI.components.fileViewer.annotations.visibility.tooltip.hide')
              : t('canvasUI.components.fileViewer.annotations.visibility.tooltip.show')
          "
          :checked-value="true"
          class="hotspot-toggle origin-center inline-block"
          size="large"
          :style="{
            '--n-box-shadow-focus': 'none',
          }"
          :unchecked-value="false"
          :value="isAnnotationVisibilityEnabled"
          @update:value="emit('toggleAnnotationVisibility')"
        >
          <template #checked>
            <span
              class="hotspot-toggle__text hotspot-toggle__text--checked tracking-wider uppercase overflow-hidden text-ellipsis whitespace-nowrap"
            >
              {{ t('canvasUI.components.fileViewer.annotations.visibility.show') }}
            </span>
          </template>
          <template #unchecked>
            <span
              class="hotspot-toggle__text hotspot-toggle__text--unchecked tracking-wider uppercase overflow-hidden text-ellipsis whitespace-nowrap"
            >
              {{ t('canvasUI.components.fileViewer.annotations.visibility.hide') }}
            </span>
          </template>
          <template #checked-icon>
            <CIcon
              class="hotspot-toggle__icon hotspot-toggle__icon--active flex items-center justify-center"
              icon="link"
              size="10"
            />
          </template>
          <template #unchecked-icon>
            <CIcon class="hotspot-toggle__icon flex items-center justify-center" icon="link" size="10" />
          </template>
        </NSwitch>
      </div>

      <div class="absolute left-[50%] top-[50%] translate-[-50%]">
        <NPagination
          v-model:page="pageModel"
          :pageCount="totalPages"
          simple
          size="large"
          @click="onClickPaginationIndex"
        />
      </div>

      <div class="flex items-center gap-2">
        <CButton
          v-tooltip="
            fileViewerSettings.fitMode === 'viewport'
              ? t('canvasUI.components.fileViewer.fitToWidth')
              : t('canvasUI.components.fileViewer.fitToViewport')
          "
          class="c-file-viewer-bottom__button"
          secondary
          size="large"
          @click="emit('fitModeToggle')"
        >
          <template #icon>
            <CIcon
              faType="fas"
              :icon="fileViewerSettings.fitMode === 'viewport' ? 'maximize' : 'arrows-left-right-to-line'"
              size="16"
            />
          </template>
        </CButton>
        <CButton
          v-tooltip="t('canvasUI.components.fileViewer.zoomOut')"
          class="c-file-viewer-bottom__button"
          secondary
          size="large"
          @click="emit('zoomOut')"
        >
          <template #icon>
            <CIcon icon="magnifying-glass-minus" size="16" />
          </template>
        </CButton>
        <CButton
          v-tooltip="t('canvasUI.components.fileViewer.zoomIn')"
          class="c-file-viewer-bottom__button"
          secondary
          size="large"
          @click="emit('zoomIn')"
        >
          <template #icon>
            <CIcon icon="magnifying-glass-plus" size="16" />
          </template>
        </CButton>
        <CButton
          v-if="isFullscreen"
          v-tooltip="t('canvasUI.components.fileViewer.exitFullscreen')"
          class="c-file-viewer-bottom__button"
          size="large"
          type="primary"
          @click="emit('exitFullscreen')"
        >
          <template #icon>
            <CIcon icon="compress" size="16" />
          </template>
        </CButton>
      </div>
    </div>
  </CCard>
</template>

<script lang="ts" setup>
import { h, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { NPagination, NSwitch } from 'naive-ui'
import type { DropdownOption } from 'naive-ui/es/dropdown/src/interface'
import type { SelectBaseOption } from 'naive-ui/lib/select/src/interface'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CCard from '@lib/components/CCard/CCard.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import type { FileViewerSettings } from '@lib/types/fileViewerSettings.types'
import type { AnnotationType } from '@lib/components/CFileViewer/CFileViewer.types'
import type { PitcherFile } from '@lib/types/file.types'
import useThemeVars from '@lib/composables/useThemeVars'
import tooltipDirective from '@lib/directives/tooltip'
import type { CLIENT_TYPE } from '@lib/constants/cdp.const'

type Props = {
  totalPages: number
  page?: number
  fileViewerSettings: FileViewerSettings
  file: PitcherFile | null
  isTextAnnotationSelected: boolean
  isLinkAnnotationSelected: boolean
  isAnnotationsSelected: boolean
  selectedAnnotationType: Exclude<AnnotationType, 'text'>
  isSearchSelected: boolean
  isThumbnailsSelected: boolean
  hideActions: boolean
  isFullscreen: boolean
  clientType?: (typeof CLIENT_TYPE)[keyof typeof CLIENT_TYPE]
  isOverlay?: boolean
  isAnnotationVisibilityEnabled: boolean
  hasHotspotsOnCurrentPage: boolean
  isHotspotToggleEnabled: boolean
  rootElement?: HTMLElement | null
}

const props = withDefaults(defineProps<Props>(), {
  page: 0,
  isOverlay: false,
})

const emit = defineEmits<{
  exitFullscreen: []
  zoomOut: []
  zoomIn: []
  setPage: [p: number]
  fitModeToggle: []
  click: []
  searchFile: []
  toggleThumbnails: []
  toggleTextAnnotation: []
  toggleLinkAnnotation: []
  toggleAnnotations: []
  selectAnnotation: [a: Exclude<AnnotationType, 'text'>]
  toggleAnnotationVisibility: []
}>()

const { t } = useI18n()
const themeVars = useThemeVars()
const vTooltip = tooltipDirective

export interface AnnotationOption extends SelectBaseOption {
  label: string
  icon: string
  value: string
}

const isAnnotationsHovered = ref(false)

const pageModel = computed({
  get: () => props.page,
  set: (v) => {
    emit('setPage', v)
  },
})

type AnnotationConfig = {
  [key in AnnotationType]: {
    icon: string
    label: string
  }
}

const annotationConfig: AnnotationConfig = {
  drawing: { icon: 'marker', label: t('canvasUI.components.fileViewer.annotations.toolbar.options.drawing') },
  freeformHighlight: {
    icon: 'highlighter',
    label: t('canvasUI.components.fileViewer.annotations.toolbar.options.freeformHighlight'),
  },
  textHighlight: {
    icon: 'input-text',
    label: t('canvasUI.components.fileViewer.annotations.toolbar.options.textHighlight'),
  },
  eraser: { icon: 'eraser', label: t('canvasUI.components.fileViewer.annotations.toolbar.options.eraser') },
  link: { icon: 'link', label: t('canvasUI.components.fileViewer.annotations.toolbar.options.link') },
  text: { icon: 'text-size', label: t('canvasUI.components.fileViewer.annotations.toolbar.options.text') },
}

const renderIcon = (icon: string) => () => h(CIcon, { icon })

const renderLabel = (option: any) => h('div', { class: 'font-500 uppercase' }, option.label)

const annotationOptions: DropdownOption[] = (['drawing', 'freeformHighlight', 'textHighlight', 'eraser'] as const).map(
  (key) => ({
    label: annotationConfig[key].label,
    key,
    icon: renderIcon(annotationConfig[key].icon),
  }),
)

const onClickPaginationIndex = (e: Event) => {
  const target = e.target

  if (target instanceof HTMLInputElement && target.classList.contains('n-input__input-el') && target.type === 'text') {
    e.stopPropagation()
  }
}
</script>

<style lang="scss" scoped>
.c-file-viewer-bottom {
  transition: opacity 0.75s;
  &--is-fullscreen {
    opacity: 0;

    &:hover {
      transition: opacity 0.1s;
      opacity: 1;
    }
  }

  &__button {
    width: 44px;
    height: 44px;

    &--selected {
      background: v-bind('themeVars.primary6');
      border: 1px solid v-bind('themeVars.primary');
    }
  }

  &__annotation-dropdown {
    background: v-bind('themeVars.primary6');
  }

  &__annotation-select {
    background: v-bind('themeVars.primary6');
    &-bordered {
      border: 1px solid v-bind('themeVars.primary');
    }
    &-hover {
      background: v-bind('themeVars.primary5');
    }
  }

  :deep(.n-pagination-quick-jumper) {
    .n-input {
      padding: 4px;
      text-align: center;
    }
  }

  // Hotspot toggle styles
  .hotspot-toggle {
    &__text {
      &--checked {
        color: v-bind('themeVars.base');
      }

      &--unchecked {
        color: v-bind('themeVars.primary');
      }
    }

    &__icon {
      color: v-bind('themeVars.primary');

      &--active {
        color: v-bind('themeVars.primary');
      }
    }
  }
}
</style>
